import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    /** Только главный тренер клуба. */
    requiresHead?: boolean;
    /** Только участник клуба (главный или тренер). */
    requiresClub?: boolean;
    guestOnly?: boolean;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/invite/:token',
      name: 'invite',
      component: () => import('@/views/InviteView.vue'),
      props: true,
    },
    {
      path: '/legal',
      name: 'legal',
      component: () => import('@/views/LegalView.vue'),
    },
    {
      path: '/admin/legal',
      name: 'admin-legal',
      component: () => import('@/views/AdminLegalView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/mark',
      name: 'mark',
      component: () => import('@/views/MarkView.vue'),
      meta: { requiresAuth: true, requiresClub: true },
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('@/views/ReportsView.vue'),
      meta: { requiresAuth: true, requiresClub: true },
    },
    {
      path: '/athletes',
      name: 'athletes',
      component: () => import('@/views/AthletesView.vue'),
      meta: { requiresAuth: true, requiresClub: true },
    },
    {
      path: '/athletes/:id',
      name: 'athlete',
      component: () => import('@/views/AthleteView.vue'),
      props: true,
      meta: { requiresAuth: true, requiresClub: true },
    },
    {
      path: '/coaches',
      name: 'coaches',
      component: () => import('@/views/CoachesView.vue'),
      meta: { requiresAuth: true, requiresHead: true },
    },
    {
      path: '/club',
      name: 'club',
      component: () => import('@/views/ClubSettingsView.vue'),
      meta: { requiresAuth: true, requiresHead: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/clubs',
      name: 'admin-clubs',
      component: () => import('@/views/AdminClubsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('@/views/AdminUsersView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  // Первый переход ждёт восстановления сессии по cookie, иначе залогиненного отправило бы на /login.
  if (!auth.ready) await auth.bootstrap();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } };
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) return { name: 'home' };
  if (to.meta.requiresHead && !auth.isHead) return { name: 'home' };
  if (to.meta.requiresClub && !auth.isCoach) return { name: 'home' };
  if (to.meta.guestOnly && auth.isAuthenticated) return { name: 'home' };
  // Тренеру домашняя не нужна — сразу в работу.
  if (to.name === 'home' && auth.isCoach) return { name: 'mark' };
  return true;
});
